import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { useHistory, useLocation } from 'react-router-dom';

import CustomLoading from '../../../util_components/custom_loading';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import SummaryCards from '../shared/summary_cards';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import TeacherFilter from '../shared/teacher_filter';
import TeacherSlugLine from '../shared/teacher_slug_line';
import StatusPill from '../shared/status_pill';
import { toggle_sort } from '../shared/use_sortable';
import ShadowBanner from './shadow_banner';
import EarningsBulkBar from './earnings_bulk_bar';
import { fetch_csv, fetch_l1 } from '../api';
import {
  csv_filename,
  extract_csv_payload,
  fmt,
  fmt_signed,
  is_permission_error,
  load_failed_text,
  needs_review,
  needs_review_reason
} from '../format';
import { NEEDS_REVIEW_TEXT, l1_view } from './adapters';
import { shows_versions_link, versions_link } from '../close_versions/params';
import { l1_invoice_cell } from '../invoices/adapters';
import {
  TEACHER_STATUS_OPTIONS,
  apply_l1_drafts,
  apply_l1_sort,
  csv_params,
  l1_params,
  l1_state_for_month
} from './params';
import {
  EARNINGS_FILTER_OPTIONS,
  earnings_pill,
  page_pick_state,
  picked_uuids,
  toggle_page,
  toggle_pick
} from './earnings_logic';

/*
 * PROD-7828 R5 — Overview Level 1: one row per teacher for the selected pay month.
 * Columns follow the R5 brief: component totals + Waivers + rounded net payable (D-3).
 * W3.6: an Invoice column (the teacher confirms it after close; the Invoices tab lists who is still awaited).
 * Email status is intentionally absent until R11 (D-4). Rows whose month failed to compute
 * or is stale show "needs review" instead of a number.
 * A-15: row checkboxes + a bulk bar show/hide the teacher app's Earnings page (the module is grant-only, so
 * every user who sees this table may use it); the selection survives paging and clears on a month change.
 */
const PAGE_SIZE = 100;

const REVIEW_OPTIONS = [{ value: 'NEEDS_REVIEW', label: 'Needs review only' }];

const OverviewLevel1 = ({
  pay_month,
  month_label,
  view_state,
  on_view_state,
  on_error,
  on_notify = () => {},
  on_open_teacher
}) => {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [load_error, setLoadError] = useState('');
  const [rows, setRows] = useState([]);
  const [stat_cards, setStatCards] = useState([]);
  const [total_count, setTotalCount] = useState(0);
  const [shadow_banner, setShadowBanner] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [picked, setPicked] = useState({});
  const page_box = useRef(null);
  // D3: drafts (applied only on Apply, house pattern), applied filters, sort and page owned by OverviewTab.
  const { applied, offset, drafts, sort } = view_state;
  const set_draft = (key, value) =>
    on_view_state((s) => ({ ...s, drafts: { ...s.drafts, [key]: value } }));

  // F4: only the latest request may render — a slow month-A response must never land under month B.
  const request_seq = useRef(0);
  // HM2-M1: last args, so the LoadFailed retry button can replay the exact request that failed.
  const last_args = useRef([0, {}, null]);

  const load = useCallback(
    (next_offset, filters, next_sort) => {
      last_args.current = [next_offset, filters, next_sort];
      request_seq.current += 1;
      const seq = request_seq.current;
      if (!pay_month) {
        // D1: never leave a previous month's rows, cards or banner on screen without a month.
        setRows([]);
        setStatCards([]);
        setTotalCount(0);
        setShadowBanner(false);
        setLoading(false);
        setLoadError('');
        return;
      }
      setLoading(true);
      setLoadError('');
      setStatCards([]);
      setShadowBanner(false);
      fetch_l1(l1_params({ pay_month, ...filters, sort: next_sort }, PAGE_SIZE, next_offset))
        .then((data) => {
          if (seq !== request_seq.current) return;
          const view = l1_view(data);
          setRows(view.rows);
          setStatCards(view.stat_cards);
          setTotalCount(view.total_count);
          setShadowBanner(view.shadow_banner);
          setLoading(false);
        })
        .catch((e) => {
          if (seq !== request_seq.current) return;
          // Permission errors also toast (module-wide gate); other failures are inline-only, matching Penalties.
          if (is_permission_error(e)) on_error(e, 'Failed to load the teacher payment overview.');
          setRows([]);
          setStatCards([]);
          setTotalCount(0);
          setLoading(false);
          // HM2-M1: an inline, retryable failure — never a silent "0 teachers this month" state.
          // CD-144: shows the server's own LOOKUP_FAILED sentence when the body carries one.
          setLoadError(load_failed_text(e, 'Failed to load the teacher payment overview.'));
        });
    },
    [pay_month, on_error]
  );

  const retry = () => load(...last_args.current);

  // (Re)mount after Back keeps the page and sort; a month change goes to page 1. Filters need an explicit Apply.
  useEffect(() => {
    setPicked({});
    const next = l1_state_for_month(view_state, pay_month);
    if (next !== view_state) on_view_state(next);
    load(next.offset, next.applied, next.sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pay_month]);

  const apply_filters = () => {
    setPicked({}); // a new filter or sort clears picks, so a bulk action never reaches teachers no longer shown
    const next = apply_l1_drafts(view_state);
    on_view_state(next);
    load(0, next.applied, next.sort);
  };

  // The teacher filter applies itself (a pick or a clear), unlike the other filters' Apply button.
  const on_teacher_change = (next_teacher) => {
    setPicked({});
    const next_state = {
      ...view_state,
      drafts: {
        ...view_state.drafts,
        search: next_teacher.teacher_uuid,
        search_display: next_teacher.display
      }
    };
    const next = apply_l1_drafts(next_state);
    on_view_state(next);
    load(0, next.applied, next.sort);
  };

  // Mirrors the chosen teacher into the URL (merged with whatever else is there), so a reload or a
  // shared link keeps the filter — the one piece of L1 state kept in the URL (D3's drafts/Apply covers
  // the rest, which are not link-shareable on their own).
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const current = qs.get('teacher_uuid') || '';
    const next = applied.search || '';
    if (current === next) return;
    if (next) qs.set('teacher_uuid', next);
    else qs.delete('teacher_uuid');
    const search = qs.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied.search]);

  const go_page = (next_offset) => {
    on_view_state((s) => ({ ...s, offset: next_offset }));
    load(next_offset, applied, sort);
  };

  const on_sort = (key) => {
    setPicked({});
    const next = apply_l1_sort(view_state, toggle_sort(sort, key));
    on_view_state(next);
    load(0, next.applied, next.sort);
  };

  // D-12: the tie-out CSV is a first-class R5 button — it is the input the parity harness
  // compares against the manual sheet. Server-generated so the export never diverges from
  // the stored numbers (and never re-derives anything client-side).
  const download_csv = () => {
    if (downloading || !pay_month) return;
    setDownloading(true);
    fetch_csv(csv_params({ pay_month, ...applied, sort }))
      .then((body) => {
        const csv = extract_csv_payload(body);
        if (!csv) {
          on_error(new Error('empty csv'), 'The CSV export came back empty.');
          setDownloading(false);
          return;
        }
        saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), csv_filename(pay_month));
        setDownloading(false);
      })
      .catch((e) => {
        on_error(e, 'Failed to download the tie-out CSV.');
        setDownloading(false);
      });
  };

  const amount_cell = (row, value, tone) => {
    if (needs_review(row)) {
      return (
        <span className="tp-neg" title={needs_review_reason(row)}>
          {NEEDS_REVIEW_TEXT}
        </span>
      );
    }
    return <span className={tone}>{value}</span>;
  };

  // D111 C10: closed now or closed before (reopen_count); a never-closed month has no versions.
  const versions_button = (t) =>
    shows_versions_link(t) ? (
      <button
        type="button"
        className="tp-link"
        aria-label={`Versions of ${t.teacher_name}'s month`}
        onClick={() => history.push(versions_link(pay_month, t.teacher_uuid))}
      >
        Versions
      </button>
    ) : null;

  const page_pick = page_pick_state(picked, rows);
  useLayoutEffect(() => {
    if (page_box.current) page_box.current.indeterminate = page_pick.some;
  }, [page_pick.some]);
  const selected_uuids = picked_uuids(picked);
  const on_earnings_done = () => {
    setPicked({});
    load(offset, applied, sort);
  };

  const columns = [
    {
      key: 'select',
      label: 'Select',
      render: (t) => (
        <input
          type="checkbox"
          aria-label={`Select ${t.teacher_name || t.teacher_uuid}`}
          checked={!!picked[t.teacher_uuid]}
          onChange={() => setPicked((p) => toggle_pick(p, t.teacher_uuid))}
        />
      )
    },
    {
      key: 'teacher',
      label: 'Teacher',
      hideInCard: true,
      sort_key: 'teacher_name',
      render: (t) => (
        <button type="button" className="tp-link" onClick={() => on_open_teacher(t)}>
          <div>{t.teacher_name || '--'}</div>
          <TeacherSlugLine slug={t.teacher_slug} />
        </button>
      )
    },
    {
      key: 'one_on_one_amount',
      label: '1-on-1',
      align: 'right',
      sort_key: 'one_on_one',
      render: (t) => amount_cell(t, fmt(t.one_on_one_amount), 'tp-pos')
    },
    {
      key: 'group_class_amount',
      label: 'GC',
      align: 'right',
      sort_key: 'group_class',
      render: (t) => amount_cell(t, fmt(t.group_class_amount), 'tp-pos')
    },
    {
      key: 'trial_amount',
      label: 'Trial',
      align: 'right',
      sort_key: 'trial',
      render: (t) => amount_cell(t, fmt(t.trial_amount), 'tp-pos')
    },
    {
      key: 'incentive_amount',
      label: 'Incentives',
      align: 'right',
      sort_key: 'incentives',
      render: (t) => amount_cell(t, fmt_signed(t.incentive_amount), 'tp-pos')
    },
    {
      key: 'additional_amount',
      label: 'Add. payments',
      align: 'right',
      sort_key: 'additional',
      render: (t) =>
        amount_cell(
          t,
          fmt_signed(t.additional_amount),
          Number(t.additional_amount) < 0 ? 'tp-neg' : 'tp-pos'
        )
    },
    {
      key: 'penalty_amount',
      label: 'Penalties',
      align: 'right',
      sort_key: 'penalties',
      render: (t) => amount_cell(t, fmt(t.penalty_amount), 'tp-neg')
    },
    {
      key: 'waived_penalty_amount',
      label: 'Waivers',
      align: 'right',
      sort_key: 'waivers',
      render: (t) => amount_cell(t, fmt(t.waived_penalty_amount), 'tp-pos')
    },
    {
      key: 'payable_amount',
      label: 'Payable',
      align: 'right',
      sort_key: 'net',
      render: (t) => amount_cell(t, fmt(t.payable_amount, 2), 'tp-strong')
    },
    {
      key: 'invoice_status',
      label: 'Invoice',
      render: (t) => {
        const cell = l1_invoice_cell(t);
        return <StatusPill tone={cell.tone}>{cell.label}</StatusPill>;
      }
    },
    {
      key: 'earnings_visible',
      label: 'Earnings',
      render: (t) => {
        const pill = earnings_pill(t);
        return <StatusPill tone={pill.tone}>{pill.label}</StatusPill>;
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      hideInCard: true,
      render: (t) => (
        <span className="tp-actions">
          <button type="button" className="tp-link" onClick={() => on_open_teacher(t)}>
            View
          </button>
          {versions_button(t)}
        </span>
      )
    }
  ];

  const page_start = total_count === 0 ? 0 : offset + 1;
  const page_end = Math.min(offset + rows.length, total_count);
  const prev_off = offset <= 0;
  const next_off = offset + PAGE_SIZE >= total_count;
  // A teacher filter opened from the URL (no picked name yet) shows the loaded row's name once it lands.
  const teacher_row = applied.search ? rows.find((r) => r.teacher_uuid === applied.search) : null;
  const teacher_display = drafts.search_display || (teacher_row ? teacher_row.teacher_name : '');

  return (
    <div>
      <ShadowBanner show={shadow_banner} />

      <SummaryCards cards={stat_cards} />

      <div className="tp-frow">
        <div style={{ width: '220px' }}>
          <TeacherFilter
            id="tp-l1-teacher-filter"
            value={{ teacher_uuid: applied.search, display: teacher_display }}
            onChange={on_teacher_change}
          />
        </div>
        <div style={{ width: '200px' }}>
          <CustomSingleSelect
            label="Rows"
            noLabel
            options={REVIEW_OPTIONS}
            value={drafts.review}
            defaultOption="All teachers"
            onChange={(e) => set_draft('review', e.target.value)}
          />
        </div>
        <div style={{ width: '220px' }}>
          <CustomSingleSelect
            label="Teacher status"
            noLabel
            options={TEACHER_STATUS_OPTIONS}
            value={drafts.status}
            defaultOption="All teacher statuses"
            onChange={(e) => set_draft('status', e.target.value)}
          />
        </div>
        <div style={{ width: '200px' }}>
          <CustomSingleSelect
            label="Earnings"
            ariaLabel="Earnings filter"
            noLabel
            options={EARNINGS_FILTER_OPTIONS}
            value={drafts.earnings || ''}
            defaultOption="Earnings: all"
            onChange={(e) => set_draft('earnings', e.target.value)}
          />
        </div>
        <button type="button" className="tp-btn-out" onClick={apply_filters}>
          Apply
        </button>
        <div className="tp-frow-right">
          {/* aria-disabled, not disabled: the pressed button keeps focus while it prepares (CD-114). */}
          <button
            type="button"
            className="tp-btn-out"
            onClick={download_csv}
            aria-disabled={downloading || !pay_month ? 'true' : undefined}
          >
            {downloading ? 'Preparing…' : '↓ Tie-out CSV'}
          </button>
        </div>
      </div>

      <div className="tp-card">
        <div className="tp-card-hd">
          <span className="tp-card-hd-title">
            All teachers · {month_label}{' '}
            <span className="tp-card-hd-sub">
              {load_error
                ? ''
                : total_count === 0
                  ? 'No teachers'
                  : `Showing ${page_start}–${page_end} of ${total_count}`}
            </span>
          </span>
          <span className="tp-card-hd-sub">Click a teacher to open the breakdown</span>
        </div>
        {!loading && !load_error && rows.length ? (
          <div className="tp-frow">
            <label className="tp-na-row-main">
              <input
                ref={page_box}
                type="checkbox"
                checked={page_pick.all}
                onChange={() => setPicked((p) => toggle_page(p, rows))}
              />
              <span>Select all on page ({rows.length})</span>
            </label>
            {rows.some((r) => 'earnings_visible' in r) ? (
              <EarningsBulkBar
                uuids={selected_uuids}
                on_clear={() => setPicked({})}
                on_done={on_earnings_done}
                on_notify={on_notify}
              />
            ) : null}
          </div>
        ) : null}
        {loading ? (
          <CustomLoading />
        ) : load_error ? (
          <LoadFailed text={load_error} on_retry={retry} />
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(t) => t.teacher_uuid}
            sort={sort}
            on_sort={on_sort}
            emptyText="No teacher payment rows for this month and filter."
            cardTitle={(t) => (
              <>
                <button
                  type="button"
                  className="tp-link"
                  style={{ fontSize: 14 }}
                  onClick={() => on_open_teacher(t)}
                >
                  {t.teacher_name || '--'}
                </button>
                {versions_button(t)}
              </>
            )}
          />
        )}
      </div>

      {total_count > PAGE_SIZE ? (
        <div className="flex items-center gap-3 justify-center mt-3">
          {/* aria-disabled, not disabled: Next on the last page keeps focus (CD-114). */}
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={prev_off ? undefined : () => go_page(Math.max(0, offset - PAGE_SIZE))}
            aria-disabled={prev_off ? 'true' : undefined}
          >
            Prev
          </button>
          <span className="tp-card-hd-sub">
            {page_start}–{page_end} / {total_count}
          </span>
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={next_off ? undefined : () => go_page(offset + PAGE_SIZE)}
            aria-disabled={next_off ? 'true' : undefined}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default OverviewLevel1;
